// Exportación reutilizable de tablas de datos a CSV / XLS / PDF.
// Uso: <ExportarBotones nombre="ventas-2026" titulo="Ventas" headers={[...]} filas={[...]} colsDerecha={[0,6]} />
// - `filas` debe incluir (si aplica) la fila de TOTALES como última fila.
// - Números crudos en las filas → CSV/XLS los exportan como número; para el PDF
//   usa `fmtPDF` para formatear (p. ej. montos con usd()).
import { useQuery } from '@tanstack/react-query';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { LOCALE } from '@/lib/utils/regional';

/** Membrete de la empresa para encabezar lo exportado. */
export interface MarcaExport {
  nombre?: string | null; ruc?: string | null;
  direccion?: string | null; telefono?: string | null;
  /** data URI; solo se dibuja en el PDF (ver nota en exportarXLSX). */
  logo?: string | null;
}

/** Trae el membrete de la empresa del usuario. Se cachea: es un dato que no
 *  cambia dentro de una sesión de trabajo. */
export function useMarcaEmpresa(): MarcaExport | undefined {
  const { user } = useAuth();
  const id = user?.empresa?.id;
  const { data } = useQuery<MarcaExport | undefined>({
    queryKey: ['marca-empresa', id],
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => (await apiClient.get<MarcaExport[]>(
      `/rest/autenticacion.empresas?id=eq.${id}&select=nombre,ruc,direccion,telefono,logo`)).data?.[0],
  });
  // mientras carga se usa lo que ya trae el token, para no exportar sin membrete
  return data ?? (user?.empresa ? { nombre: user.empresa.nombre, logo: user.empresa.logo } : undefined);
}

/** Líneas de texto del membrete, para los formatos que no admiten imagen. */
const lineasMarca = (m?: MarcaExport): string[] => {
  if (!m) return [];
  const l: string[] = [];
  if (m.nombre) l.push(m.nombre);
  const id = [m.ruc ? `RUC ${m.ruc}` : '', m.direccion ?? '', m.telefono ?? ''].filter(Boolean).join(' · ');
  if (id) l.push(id);
  return l;
};

export type Celda = string | number | null;

const escCSV = (v: Celda) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;             // anti-inyección de fórmulas
  if (/[",\n;]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
};

export function exportarCSV(nombre: string, headers: string[], filas: Celda[][], marca?: MarcaExport) {
  const cab = lineasMarca(marca).map((l) => [l]);
  const csv = [...cab, ...(cab.length ? [[]] : []), headers, ...filas]
    .map((r) => r.map(escCSV).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a'); a.href = url; a.download = `${nombre}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export async function exportarXLSX(
  nombre: string, headers: string[], filas: Celda[][], hoja = 'Datos',
  marca?: MarcaExport, titulo?: string, subtitulo?: string,
) {
  const XLSX = await import('xlsx');
  // El membrete va como filas de texto: SheetJS en su versión libre NO incrusta
  // imágenes, así que el logo solo sale en el PDF. Mejor decirlo que fingirlo.
  const cab: Celda[][] = [
    ...lineasMarca(marca).map((l) => [l] as Celda[]),
    ...(titulo ? [[titulo] as Celda[]] : []),
    ...(subtitulo ? [[subtitulo] as Celda[]] : []),
  ];
  const ws = XLSX.utils.aoa_to_sheet([...cab, ...(cab.length ? [[]] : []), headers, ...filas]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja.slice(0, 31));
  XLSX.writeFile(wb, `${nombre}.xlsx`);
}

const escH = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

export function exportarPDF(titulo: string, headers: string[], filas: Celda[][], opts: {
  subtitulo?: string; colsDerecha?: number[]; fmt?: (v: Celda, col: number) => string;
  marca?: MarcaExport;
} = {}) {
  const der = new Set(opts.colsDerecha ?? []);
  const fmt = opts.fmt ?? ((v: Celda) => (v == null ? '' : String(v)));
  const th = headers.map((h, i) => `<th class="${der.has(i) ? 'r' : ''}">${escH(h)}</th>`).join('');
  const body = filas.map((r) => `<tr>${r.map((c, i) => `<td class="${der.has(i) ? 'r' : ''}">${escH(fmt(c, i))}</td>`).join('')}</tr>`).join('');
  const m = opts.marca;
  const membrete = m ? `<div class="mb">
      ${m.logo ? `<img class="logo" src="${escH(m.logo)}" alt="">` : ''}
      <div>
        <div class="emp">${escH(m.nombre ?? '')}</div>
        ${lineasMarca(m).slice(1).map((l) => `<div class="dat">${escH(l)}</div>`).join('')}
      </div>
    </div>` : '';
  const w = window.open('', '_blank'); if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escH(titulo)}</title>
    <style>body{font-family:system-ui,Arial,sans-serif;padding:20px;font-size:12px}h2{margin:0 0 4px}p{margin:0 0 12px;color:#555}
    table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ddd;padding:5px 7px;text-align:left}
    th{background:#f3f4f6}.r{text-align:right}
    .mb{display:flex;align-items:center;gap:14px;border-bottom:1px solid #999;padding-bottom:10px;margin-bottom:12px}
    .logo{height:56px;width:56px;object-fit:contain}
    .emp{font-size:15px;font-weight:700}.dat{font-size:10px;color:#555}
    .pie{margin-top:14px;font-size:9px;color:#777}
    @media print{@page{margin:12mm}}</style></head>
    <body>${membrete}<h2>${escH(titulo)}</h2>${opts.subtitulo ? `<p>${escH(opts.subtitulo)}</p>` : ''}
    <table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>
    <div class="pie">Generado el ${escH(new Date().toLocaleString(LOCALE))}</div></body></html>`);
  w.document.close();
  // si hay logo se espera a que cargue: imprimir antes deja el membrete en blanco
  const imprimir = () => { w.focus(); w.print(); };
  const img = w.document.images[0];
  if (img && !img.complete) { img.onload = imprimir; img.onerror = imprimir; }
  else setTimeout(imprimir, 300);
}

export function ExportarBotones({ nombre, titulo, headers, filas, colsDerecha, subtitulo, fmtPDF, disabled, marca }: {
  nombre: string; titulo: string; headers: string[]; filas: Celda[][];
  colsDerecha?: number[]; subtitulo?: string; fmtPDF?: (v: Celda, col: number) => string; disabled?: boolean;
  /** Membrete de la empresa (logo + RUC…) al encabezar lo exportado.
   *  Se pasa explícitamente: así una pantalla lo activa sin cambiar las demás.
   *  Usa el hook `useMarcaEmpresa()` para obtenerlo. */
  marca?: MarcaExport;
}) {
  const off = disabled || filas.length === 0;
  return (
    <div className="flex items-center gap-1 no-print">
      <Button size="sm" variant="outline" disabled={off} onClick={() => void exportarXLSX(nombre, headers, filas, 'Datos', marca, titulo, subtitulo)} title="Exportar a Excel"><FileSpreadsheet className="w-4 h-4 mr-1" /> XLS</Button>
      <Button size="sm" variant="outline" disabled={off} onClick={() => exportarPDF(titulo, headers, filas, { subtitulo, colsDerecha, fmt: fmtPDF, marca })} title="Exportar a PDF"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
      <Button size="sm" variant="outline" disabled={off} onClick={() => exportarCSV(nombre, headers, filas, marca)} title="Exportar a CSV"><Download className="w-4 h-4 mr-1" /> CSV</Button>
    </div>
  );
}
